import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { ChangeRequestFilter } from '../../../features/changeRequests/ChangeRequestFilter';
import CustomTable from '../../../components/common/CustomTable';
import { columns } from '../columns';
import {
    clearFilters,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    setFilters,
} from '../../../store/slices/tableController';
import { useSnackbar } from 'notistack';
import { useSearchChangeRequestsMutation } from '../../../api/changeRequests';

const ApproverChangeRequests = ({ setCRStatus }) => {
    const [changeRequests, setChangeRequests] = useState({});
    const [selectedCRs, setSelectedCRs] = useState([]);
    const controller = useSelector((state) => state.tableController);
    const currentUser = useSelector((state) => state.session.user);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [selectedPosition, setSelectedPosition] = useState([]);
    const dispatch = useDispatch();

    const [searchChangeRequests, { isLoading }] = useSearchChangeRequestsMutation();

    const fetchData = useCallback(async (searched, filters, sort, sortBy, page, pageSize) => {
        try {
            const body = new FormData();
            body.append('s', 'Active');
            body.append('a', currentUser?.empDBID);

            const query = new URLSearchParams({});
            if (filters.length) {
                filters.forEach((filter) => query.append(filter.property, filter.value));
            }
            if (sortBy) {
                query.append('sort', sortBy);
                query.append('order', sort);
            }
            query.append('offset', page * pageSize);
            query.append('max', pageSize);

            const response = await searchChangeRequests({ query, body }).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            setChangeRequests(response);
            response?.statusWiseStats && setCRStatus(response?.statusWiseStats);
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
    }, []);

    useEffect(() => {
        if (fetchData)
            fetchData(
                controller.searched,
                controller.filters,
                controller.sort,
                controller.sortBy,
                controller.page,
                controller.pageSize,
            );
    }, [
        controller.searched,
        controller.filters,
        controller.sort,
        controller.sortBy,
        controller.page,
        controller.pageSize,
        fetchData,
    ]);

    const handleFiltersApply = (newFilters) => {
        const parsedFilters = newFilters.map((filter) => ({
            property: filter.property.name,
            value: filter.value,
            operator: filter.operator.value,
        }));

        dispatch(setFilters(parsedFilters));
    };

    const handleFiltersClear = () => {
        dispatch(clearFilters());
    };

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleSelect = (event, rowId, row) => {
        const isChecked = event.target.checked;
        if (isChecked) {
            setSelectedCRs((prev) => [...prev, rowId]);
            if (row?.categoryType?.name === 'Major')
                setSelectedPosition((prev) => {
                    if (prev.filter((item) => item.id === row?.parentEntityId).length === 0)
                        return [
                            ...prev,
                            {
                                positionCode: row?.position?.positionCode,
                                id: row?.parentEntityId,
                                changeRequestId: row.id,
                            },
                        ];
                    else return prev;
                });
        } else {
            setSelectedCRs((prev) => prev.filter((id) => id !== rowId));
            if (row?.categoryType?.name === 'Major')
                setSelectedPosition((prev) => prev.filter((item) => item.id !== row?.parentEntityId));
        }
    };

    const handleSelectAll = (event) => {
        const isChecked = event.target.checked;
        if (isChecked) {
            let selectedCRs = [];
            let selectedPosition = [];
            changeRequests?.changeRequests.forEach((row) => {
                selectedCRs.push(row['id']);
                if (
                    selectedPosition.filter((item) => item.id === row?.parentEntityId).length === 0 &&
                    row?.categoryType?.name === 'Major'
                )
                    selectedPosition.push({
                        id: row?.parentEntityId,
                        positionCode: row?.position?.positionCode,
                        changeRequestId: row.id,
                    });
            });
            setSelectedPosition(selectedPosition);
            setSelectedCRs(selectedCRs);
        } else {
            setSelectedPosition([]);
            setSelectedCRs([]);
        }
    };

    console.log(selectedPosition);

    return (
        <>
            <ChangeRequestFilter
                tab={'approver'}
                disabled={false}
                filters={controller.filters}
                onFiltersApply={handleFiltersApply}
                onFiltersClear={handleFiltersClear}
                selectedCRs={selectedCRs}
                selectedPosition={selectedPosition}
                filterProperties={columns}
                initialFilters={controller.initialFilters}
                handleBulkAction={() => {
                    setSelectedCRs([]);
                    fetchData(
                        controller.searched,
                        controller.filters,
                        controller.sort,
                        controller.sortBy,
                        controller.page,
                        controller.pageSize,
                    );
                }}
            />
            <CustomTable
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onSelect={handleSelect}
                onSelectAll={handleSelectAll}
                onSortChange={handleSortChange}
                page={controller.page + 1}
                onRowClick={(row) => {
                    navigate(`/changeRequests/${row}?type=approver`);
                }}
                columns={columns}
                rows={changeRequests?.changeRequests || []}
                rowsCount={changeRequests?.changeRequestCount || 0}
                selectedRows={selectedCRs}
                sort={controller.sort}
                sortBy={controller.sortBy}
                showRowSelector
                showPagignation
                keyColumn="id"
                route="changerequests"
                action={() => {
                    setSelectedCRs([]);
                    fetchData(
                        controller.searched,
                        controller.filters,
                        controller.sort,
                        controller.sortBy,
                        controller.page,
                        controller.pageSize,
                    );
                }}
            />
        </>
    );
};

export default ApproverChangeRequests;
